import {
  useAuthenticateOrganizationMutation,
  useListOrganizationsFromDiscoveryMutation,
} from "@graphql/auth/auth.g";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Icon } from "@design-system/Icon/Icon";
import { Spinner } from "@design-system/Spinner/Spinner";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import { STORAGE_KEYS } from "@utils/storageKeys";

import classes from "./AuthOrganizationsCard.module.css";

export const AuthOrganizationsCard = () => {
  const intermediateSessionToken = sessionStorage.getItem(
    STORAGE_KEYS.INTERMEDIATE_SESSION_TOKEN,
  );

  const authenticateOrganizationMutation =
    useAuthenticateOrganizationMutation();
  const listOrganizationsFromDiscoveryMutation =
    useListOrganizationsFromDiscoveryMutation();
  const navigate = useNavigate();

  const listOrganizationsFromDiscovery =
    listOrganizationsFromDiscoveryMutation.mutateAsync;

  const discoveredOrganizations =
    listOrganizationsFromDiscoveryMutation.data?.listOrganizationsFromDiscovery
      ?.discoveredOrganizations;

  useEffect(() => {
    (async () => {
      if (!intermediateSessionToken) return;

      await listOrganizationsFromDiscovery({
        intermediateSessionToken,
      });
    })();
  }, [intermediateSessionToken, listOrganizationsFromDiscovery]);

  const handleOrganizationClick = useCallback(
    async (organizationId: string) => {
      if (!intermediateSessionToken) return;

      await authenticateOrganizationMutation.mutateAsync({
        token: intermediateSessionToken,
        stytchOrganizationId: organizationId,
      });

      navigate(routes.portal.gettingStarted.$path(), { viewTransition: true });
    },
    [navigate, intermediateSessionToken, authenticateOrganizationMutation],
  );

  return (
    <Card shadow={false} stretch>
      {!authenticateOrganizationMutation.isPending &&
        !authenticateOrganizationMutation.isSuccess && (
          <Card.Header>
            <Card.Header.Title>
              Select an organization to sign in
            </Card.Header.Title>
          </Card.Header>
        )}

      <Card.Body>
        {authenticateOrganizationMutation.isPending ||
        authenticateOrganizationMutation.isSuccess ? (
          <div className={classes.spinner}>
            <div className={classes.text}>
              <Icon icon="faIdBadge" size="sm" />
              <Text uppercase size={12} weight={500} spacing="relaxed">
                Signing in securely
              </Text>
            </div>

            <Spinner />
          </div>
        ) : (
          <div className={classes.organizations}>
            {listOrganizationsFromDiscoveryMutation.isPending && (
              <Box justify="center" align="center" stretch>
                <Spinner />
              </Box>
            )}

            {discoveredOrganizations?.map((discoveredOrganization) => (
              <Clickable
                key={discoveredOrganization?.organizationId}
                onClick={() => {
                  handleOrganizationClick(
                    discoveredOrganization?.organizationId || "",
                  );
                }}
                stretch
              >
                <div className={classes.organization}>
                  <Text weight={500}>
                    {discoveredOrganization?.organizationName || ""}
                  </Text>
                </div>
              </Clickable>
            ))}
          </div>
        )}
      </Card.Body>
      {!authenticateOrganizationMutation.isPending &&
        !authenticateOrganizationMutation.isSuccess && (
          <Card.Footer border>
            <Button
              variant="outlined"
              onClick={() => {
                navigate(routes.login.createOrganization.$path(), {
                  viewTransition: true,
                });
              }}
              uppercase
            >
              Create Organization
            </Button>
          </Card.Footer>
        )}
    </Card>
  );
};
